import React, { useEffect, useState } from 'react';
import isEqual from 'fast-deep-equal';
import styled from 'styled-components';
import Downshift from 'downshift';
import Skeleton from 'react-loading-skeleton';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List from 'react-virtualized/dist/commonjs/List';

import {
  DelegatorResult,
  MapValidators,
  Validator,
  ValidatorWithInfo,
} from '@block-explorer/entities';
import {
  ArrowDownIcon,
  BaseProps,
  CaptionText,
  CrossIcon,
  FlexBox,
  FlexColumn,
  FlexRow,
  Input,
  SearchIcon,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';

import ValidatorInfoRow from '../validator-info-row/validator-info-row';
import ValidatorUndelegateInfoRow from '../validator-info-row/validator-undelegate-info-row';
import { useTranslation } from 'react-i18next';
import {
  useGetBidderDetailsData,
  useGetValidatorsAuctionData,
} from '../../hooks/event-store-hooks';
import { indexBy } from 'remeda';
import { useClickAndTouchAway } from './hooks/useClickAndTouchAway';
import { useGetLastBlockInfo } from '../../hooks';
import { isValidPublicKey } from '../../utils/account';

const StyledContainer = styled.div<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    position: 'relative',
    outline: 'none',
    ...(disabled && {
      opacity: 0.5,
    }),
  })
);

const StyledFlexColumn = styled(FlexColumn)(() => ({
  minHeight: 72,
}));

const InputContainer = styled.div(() => ({
  minHeight: 36,
  maxHeight: 88,
}));

const StyledBorderedWrapper = styled(InputContainer)<{ error?: boolean }>(
  ({ theme, error }) => ({
    width: '100%',
    border: error ? `1px solid ${theme.styleguideColors.borderRed}` : 'none',
    borderRadius: theme.borderRadius.base,
  })
);

const WidthContainer = styled.div(() => ({
  width: '100%',
}));

const StyledSearchIcon = styled.div(({ theme }) => ({
  color: theme.styleguideColors.contentTertiary,
}));

const InputIconRow = styled(FlexRow)(({ theme }) => ({
  width: '48px',
}));

const ArrowSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentPrimary,
  },
  marginLeft: 16,
}));

const ClearSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentTertiary,
  },
}));

const StyledInput = styled.div(() => ({
  width: '100%',
  border: 'none',
  background: 'transparent',
}));

const MenuEmptyContainer = styled.div(({ theme }) => ({
  padding: '8px 16px',
  pointerEvents: 'none',
}));

const MenuContainer = styled.div<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  display: isOpen ? 'block' : 'none',
  marginTop: 4,
  borderRadius: theme.borderRadius.base,
  overflowY: 'auto',
  maxHeight: 236,
  position: 'absolute',
  top: '60px',
  width: '100%',
  zIndex: 1,
}));

const HeadersContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: 36,
  background: theme.styleguideColors.fillSecondary,
  padding: '0 16px',
}));

const StyledTh = styled.div<{
  align?: React.CSSProperties['textAlign'];
  width?: React.CSSProperties['width'];
}>(({ theme, align, width }) => ({
  textAlign: align || 'left',
  ...(width && { width }),
}));

const DataHeader = ({ children, ...props }) => (
  <StyledTh {...props}>
    <CaptionText size={1}>{children}</CaptionText>
  </StyledTh>
);

const ITEM_HEIGHT = 48;
const MOBILE_ITEM_HEIGHT = 76;
const MAX_VISIBLE_ITEMS_AMOUNT = 4;

enum InputType {
  selectedItem = 'selectedItem',
  input = 'input',
}

/* eslint-disable-next-line */
export interface ValidatorDropdownProps extends BaseProps {
  accountDelegations?: DelegatorResult[] | null;
  label?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  placeholder?: string;
  noItemsMessage?: string;
  value?: ValidatorWithInfo | null;
  filterByDelegatorPublicKey?: string;
  onSelect?: (item: ValidatorWithInfo | null) => void;
}

// TODO: we need to add test and/or refactor it
export function ValidatorDropdown(props: ValidatorDropdownProps) {
  const { t } = useTranslation();
  const {
    label = t('Validator'),
    placeholder = t('Search by public key or CSPR.name'),
    noItemsMessage = t('No Validators!'),
    filterByDelegatorPublicKey,
    accountDelegations = null,
    disabled,
    value,
    errorMessage,
    onSelect,
  } = props;
  const [validatorInput, setValidatorInput] = useState<string>('');
  const [inputType, setInputType] = useState<InputType>(
    value ? InputType.selectedItem : InputType.input
  );
  const [validators, setValidators] = useState<Validator[] | null>(null);

  const { ref: outerClickRef } = useClickAndTouchAway({
    callback: () => {
      if (value && inputType === InputType.input) {
        setInputType(InputType.selectedItem);
      } else if (!value && validatorInput && inputType === InputType.input) {
        setValidators(null);
      }
    },
  });

  const { data: lastBlock } = useGetLastBlockInfo();
  const auctionEraId = lastBlock && lastBlock?.era_id + 2;

  const { data: bids, loading: bidsLoading } = useGetValidatorsAuctionData(
    auctionEraId
      ? {
          era_id: auctionEraId,
          includes: 'account_info,cspr_name',
          page_size: 100,
          is_active: 'true',
        }
      : null
  );

  const { data: bidder, loading: bidderDataLoading } = useGetBidderDetailsData(
    isValidPublicKey(validatorInput) && auctionEraId
      ? {
          publicKey: validatorInput,
          era_id: auctionEraId,
          includes: 'account_info,cspr_name',
          is_active: 'true',
        }
      : null
  );

  useEffect(() => {
    if (!value && inputType === InputType.selectedItem) {
      setInputType(InputType.input);
    }
  }, [value]);

  useEffect(() => {
    if (bids?.length && !value) {
      setValidators(bids);
    }
  }, [bids]);

  useEffect(() => {
    if (isValidPublicKey(validatorInput) && bidder && bidder.data.active) {
      setValidators([bidder.data]);
    } else if (!bidder?.data) {
      setValidators(bids);
    }
  }, [bidder?.data, validatorInput]);

  const delegators: any = accountDelegations
    ? accountDelegations
        .map((delegator) => ({
          ...delegator.bidder,
          ...{
            account_info: { ...delegator.validator_account_info },
            cspr_name: delegator.validator_cspr_name,
          },
        }))
        .filter((delegatorExtendedInfo) => delegatorExtendedInfo != null)
    : null;

  const sortedDelegatorsList =
    (filterByDelegatorPublicKey != null && delegators
      ? delegators.map(MapValidators)
      : validators) || [];

  const handleClearInput = () => {
    setValidatorInput('');
    setValidators(null);
    onSelect && onSelect(null);
  };

  const handleChangeInput = (input) => {
    if (input == '' || input == null) {
      setValidatorInput('');
      onSelect && onSelect(null);
    } else {
      setValidatorInput(input);
    }
  };

  const accountDelegationsByPublicKey = indexBy(
    accountDelegations || [],
    (i) => i.validator_public_key
  );

  const itemValueGetter = (item: ValidatorWithInfo) => item.key;
  const itemNameGetter = (item: ValidatorWithInfo) =>
    item.accountInfo?.info?.owner?.name;

  const handleItemSelect = (item) => {
    setInputType(InputType.selectedItem);
    onSelect && onSelect(item);
  };

  const handleChangeSelectedItemToInput = () => {
    setInputType(InputType.input);
  };

  const getItemToString = (item) => {
    if (item === null) return '';
    return typeof item === 'string' ? item : item.key;
  };

  const aboveHeader = (
    <HeadersContainer>
      <FlexRow itemsSpacing={12} grow={1}>
        <DataHeader width="40%">Validator</DataHeader>
        <DataHeader width="20%" align="right">
          Fee
        </DataHeader>
        <DataHeader width="35%" align="right">
          {filterByDelegatorPublicKey != null
            ? 'Staked Amount'
            : 'Total stake (self stake %)'}
        </DataHeader>
      </FlexRow>
    </HeadersContainer>
  );

  const mobileHeader = (
    <HeadersContainer>
      <FlexRow itemsSpacing={12} grow={1} justify="center">
        <DataHeader width="60%">
          <FlexBox align="center" style={{ height: '100%' }}>
            Validator & fee
          </FlexBox>
        </DataHeader>
        <DataHeader width="40%" align="right">
          <FlexColumn>
            <CaptionText size={1}>
              {filterByDelegatorPublicKey != null
                ? 'Staked Amount'
                : 'Total stake'}
            </CaptionText>
            <CaptionText size={1} variation="darkGray">
              (self stake %)
            </CaptionText>
          </FlexColumn>
        </DataHeader>
      </FlexRow>
    </HeadersContainer>
  );

  const responsiveHeader = useMatchMedia<any>(
    [mobileHeader, mobileHeader, aboveHeader, aboveHeader],
    []
  );
  const rowHeight = useMatchMedia(
    [MOBILE_ITEM_HEIGHT, MOBILE_ITEM_HEIGHT, ITEM_HEIGHT],
    []
  );

  return (
    <Downshift
      itemToString={getItemToString}
      onSelect={handleItemSelect}
      selectedItem={value}
      selectedItemChanged={(prevItem, item) => !isEqual(prevItem, item)}
      onInputValueChange={handleChangeInput}
    >
      {(downshift) => {
        const {
          getItemProps,
          getMenuProps,
          isOpen,
          selectedItem,
          getRootProps,
          getInputProps,
          getToggleButtonProps,
          getLabelProps,
          highlightedIndex,
        } = downshift;

        const visibleItems = validatorInput
          ? (sortedDelegatorsList || []).filter(
              (item) =>
                itemValueGetter(item)
                  .toLowerCase()
                  .includes(validatorInput.toLowerCase()) ||
                itemNameGetter(item)
                  ?.toLowerCase()
                  .includes(validatorInput.toLowerCase())
            )
          : sortedDelegatorsList;
        const noItems = !(visibleItems && visibleItems.length);

        const handleFocus = (ev) => {
          if (disabled) {
            return;
          }
          !isOpen && downshift.openMenu();
        };

        const handleClickSelectedItem = (ev) => {
          handleChangeSelectedItemToInput(ev);
          handleFocus(ev);
        };

        const inputPrefixIcon = (
          <StyledSearchIcon>
            <SvgIcon
              src={SearchIcon}
              role={'img'}
              alt={'Magnifying glass icon'}
            />
          </StyledSearchIcon>
        );

        const inputSuffixIcon = (
          <InputIconRow justify={'end'}>
            {validatorInput && (
              <ClearSvgIcon
                src={CrossIcon}
                onClick={handleClearInput}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    handleClearInput();
                  }
                }}
                role={'img'}
                alt={'Cross icon to clear'}
                tabIndex={0}
              />
            )}
            <ArrowSvgIcon
              src={ArrowDownIcon}
              alt={'Arrow icon'}
              role={'img'}
              rotate={isOpen}
              tabIndex={0}
              {...getToggleButtonProps()}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  handleFocus(ev);
                }
              }}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            />
          </InputIconRow>
        );

        return (
          <StyledContainer disabled={disabled} ref={outerClickRef}>
            <StyledFlexColumn gap={4}>
              {label && (
                <BodyText size={1} scale={'sm'} {...getLabelProps()}>
                  {label}
                </BodyText>
              )}
              <InputContainer {...getRootProps({}, { suppressRefError: true })}>
                <FlexRow>
                  {inputType === InputType.input ? (
                    <WidthContainer>
                      <FlexRow align="center" itemsSpacing={8} grow={1}>
                        <StyledInput>
                          <Input
                            id={'validator-input'}
                            prefixIcon={inputPrefixIcon}
                            suffixIcon={inputSuffixIcon}
                            {...(getInputProps({
                              value: validatorInput || '',
                              disabled,
                            }) as any)}
                            placeholder={placeholder}
                            onFocus={handleFocus}
                          />
                        </StyledInput>
                      </FlexRow>
                    </WidthContainer>
                  ) : (
                    <WidthContainer onClick={handleClickSelectedItem}>
                      {selectedItem && (
                        <StyledBorderedWrapper error={errorMessage != null}>
                          {filterByDelegatorPublicKey != null ? (
                            <ValidatorUndelegateInfoRow
                              variation="gray"
                              validator={selectedItem}
                              stakedAmountToDelegator={
                                accountDelegationsByPublicKey[selectedItem.key]
                                  ?.stake
                              }
                              disabled={disabled}
                              isSelectedItem
                              handleOpenMenu={handleClickSelectedItem}
                            />
                          ) : (
                            <ValidatorInfoRow
                              validator={selectedItem}
                              isCopyEnabled
                              isSelectedItem
                              variation="gray"
                              disabled={disabled}
                              handleOpenMenu={handleClickSelectedItem}
                            />
                          )}
                        </StyledBorderedWrapper>
                      )}
                    </WidthContainer>
                  )}
                </FlexRow>
              </InputContainer>
            </StyledFlexColumn>
            <MenuContainer {...getMenuProps()} isOpen={isOpen} tabIndex={0}>
              {isOpen &&
                (bidsLoading || bidderDataLoading ? (
                  <MenuEmptyContainer>
                    <BodyText size={3} variation="darkGray">
                      <Skeleton height={rowHeight} />
                    </BodyText>
                  </MenuEmptyContainer>
                ) : noItems ? (
                  <MenuEmptyContainer>
                    <BodyText size={3} variation="darkGray">
                      {noItemsMessage}
                    </BodyText>
                  </MenuEmptyContainer>
                ) : (
                  <>
                    {responsiveHeader}
                    <AutoSizer disableHeight>
                      {({ width }) => (
                        <List
                          overscanRowCount={4}
                          height={Math.min(
                            MAX_VISIBLE_ITEMS_AMOUNT * rowHeight,
                            visibleItems.length * rowHeight
                          )}
                          rowCount={visibleItems.length}
                          rowHeight={rowHeight}
                          rowRenderer={({ index, style }) => {
                            const item = visibleItems[index];
                            const isHighlighted =
                              highlightedIndex !== null &&
                              highlightedIndex === index;
                            return filterByDelegatorPublicKey != null ? (
                              <ValidatorUndelegateInfoRow
                                key={item.key}
                                style={style}
                                {...getItemProps({ item, index })}
                                validator={item}
                                stakedAmountToDelegator={
                                  accountDelegationsByPublicKey[item.key].stake
                                }
                                variation="gray"
                                isHighlighted={isHighlighted}
                              />
                            ) : (
                              <ValidatorInfoRow
                                style={style}
                                {...getItemProps({ item, index })}
                                key={item.key}
                                isHighlighted={isHighlighted}
                                validator={item}
                                variation="gray"
                              />
                            );
                          }}
                          width={width}
                        />
                      )}
                    </AutoSizer>
                  </>
                ))}
            </MenuContainer>
            {!isOpen && errorMessage && (
              <BodyText size={2} variation={'red'}>
                {errorMessage}
              </BodyText>
            )}
          </StyledContainer>
        );
      }}
    </Downshift>
  );
}

export default ValidatorDropdown;
